import { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';

interface IUserOption {
  id: string;
  username: string;
  displayName: string;
  avatar?: string | null;
}

interface IUserAutocompleteProps {
  /** Currently selected user ID */
  value: string;
  /** Callback when a user is selected */
  onChange: (userId: string) => void;
  /** Async function to fetch users, receives search query */
  fetchUsers: (search?: string) => Promise<IUserOption[]>;
  /** Placeholder text for the search input */
  placeholder?: string;
  /** Placeholder text for the trigger button when no user is selected */
  triggerPlaceholder?: string;
  /** Optional className for the outer wrapper */
  className?: string;
}

const AVATAR_COLOR = '#3b82f6';

const UserAutocomplete = ({
  value,
  onChange,
  fetchUsers,
  placeholder = 'Search users...',
  triggerPlaceholder = 'Select a user',
  className = '',
}: IUserAutocompleteProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState<IUserOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IUserOption | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Load users on open and on search change
  useEffect(() => {
    if (!isOpen) return;

    const load = async () => {
      setLoading(true);

      try {
        const result = await fetchUsers(search || undefined);
        setUsers(result);
      } catch {
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(load, 200);
    return () => clearTimeout(debounce);
  }, [isOpen, search, fetchUsers]);

  // Resolve selected user on mount / value change
  useEffect(() => {
    if (!value) {
      setSelectedUser(null);
      return;
    }

    // Try to find in already loaded users
    const found = users.find((u) => u.id === value);

    if (found) {
      setSelectedUser(found);
    } else if (value && !selectedUser) {
      // Fetch to resolve
      fetchUsers().then((all) => {
        const match = all.find((u) => u.id === value);

        if (match) setSelectedUser(match);
      });
    }
  }, [value, users]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when opened
  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (user: IUserOption) => {
    setSelectedUser(user);
    onChange(user.id);
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border border-input rounded-md text-sm bg-input-background text-left flex items-center justify-between hover:border-gray-300 focus:outline-none focus:border-ring focus:ring-ring/50 focus:ring-[3px] h-9"
      >
        {selectedUser ? (
          <span className="flex items-center gap-2">
            <span
              className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-medium shrink-0"
              style={{
                backgroundColor: AVATAR_COLOR,
              }}
            >
              {selectedUser.displayName.charAt(0).toUpperCase()}
            </span>
            <span>
              {selectedUser.displayName}{' '}
              <span className="text-gray-400">@{selectedUser.username}</span>
            </span>
          </span>
        ) : (
          <span className="text-gray-400">{triggerPlaceholder}</span>
        )}
        <svg
          className="w-4 h-4 text-gray-400 shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 9l4-4 4 4m0 6l-4 4-4-4"
          />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-border rounded-lg shadow-lg overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <svg
                className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <Input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={placeholder}
                className="pl-8"
              />
            </div>
          </div>

          {/* User list */}
          <div className="max-h-60 overflow-y-auto">
            {loading ? (
              <div className="px-4 py-3 text-sm text-gray-400 text-center">
                Loading...
              </div>
            ) : users.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-400 text-center">
                No users found
              </div>
            ) : (
              users.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => handleSelect(user)}
                  className={`w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 text-left ${
                    value === user.id ? 'bg-blue-50' : ''
                  }`}
                >
                  <span
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium shrink-0"
                    style={{
                      backgroundColor: AVATAR_COLOR,
                    }}
                  >
                    {user.displayName.charAt(0).toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {user.displayName}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      @{user.username}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAutocomplete;
